import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Clock,
  Eye,
  EyeOff,
  Info,
  Loader2,
  Lock,
  LucideIcon,
} from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import { AUTH_PRODUCT_NAME } from "./authProduct";

const cx = (...parts: Array<string | false | null | undefined>) =>
  parts.filter(Boolean).join(" ");

/* ─────────────────────────────  Cabeçalho  ───────────────────────────── */

interface AuthHeaderProps {
  title: string;
  subtitle?: string;
}

export const AuthHeader: React.FC<AuthHeaderProps> = ({ title, subtitle }) => (
  <div className="space-y-1.5 text-center sm:text-left">
    {/* Eyebrow: em qual produto o usuário está entrando */}
    <p className="mb-0.5 flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-purple-700 sm:justify-start">
      <span
        aria-hidden="true"
        className="h-0.5 w-[18px] rounded-full bg-purple-700"
      />
      {AUTH_PRODUCT_NAME}
    </p>
    <h1 className="text-2xl font-bold leading-tight tracking-tight text-slate-900">
      {title}
    </h1>
    {subtitle && (
      <p className="text-sm leading-relaxed text-slate-500">{subtitle}</p>
    )}
  </div>
);

/* ───────────────────────────────  Campos  ────────────────────────────── */

interface AuthFieldProps {
  label: string;
  htmlFor: string;
  required?: boolean;
  /** Elemento à direita do rótulo (ex.: "Esqueceu a senha?"). */
  aside?: React.ReactNode;
  hint?: string;
  children: React.ReactNode;
}

export const AuthField: React.FC<AuthFieldProps> = ({
  label,
  htmlFor,
  required,
  aside,
  hint,
  children,
}) => (
  <div className="space-y-1.5">
    <div className="flex items-center justify-between">
      <label htmlFor={htmlFor} className="text-sm font-semibold text-slate-700">
        {label}
        {required && <span className="ml-1 text-rose-500">*</span>}
      </label>
      {aside}
    </div>
    {children}
    {hint && <p className="text-xs text-slate-500">{hint}</p>}
  </div>
);

const inputBase =
  "flex h-11 w-full rounded-lg border border-slate-300 bg-white text-sm text-slate-900 transition-all placeholder:text-slate-400 " +
  "hover:border-slate-400 focus:border-violet-500 focus:outline-hidden focus:ring-[3px] focus:ring-violet-500/15 " +
  "disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400 read-only:bg-slate-50 read-only:text-slate-500";

const inputError =
  "border-rose-400! text-rose-900 focus:border-rose-500! focus:ring-rose-500/15";

const iconWrap =
  "pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400 transition-colors group-focus-within:text-violet-600";

export interface AuthInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  icon?: LucideIcon;
}

export const AuthInput = React.forwardRef<HTMLInputElement, AuthInputProps>(
  ({ className, error, icon: Icon, ...props }, ref) => (
    <div className="group relative">
      {Icon && (
        <div className={iconWrap}>
          <Icon className="h-4 w-4" aria-hidden="true" />
        </div>
      )}
      <input
        ref={ref}
        className={cx(
          inputBase,
          Icon ? "pl-10 pr-3" : "px-3",
          error && inputError,
          className,
        )}
        {...props}
      />
    </div>
  ),
);
AuthInput.displayName = "AuthInput";

export const PasswordInput = React.forwardRef<
  HTMLInputElement,
  Omit<AuthInputProps, "icon" | "type">
>(({ className, error, ...props }, ref) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [capsLock, setCapsLock] = useState(false);

  const trackCaps = (
    e:
      | React.KeyboardEvent<HTMLInputElement>
      | React.MouseEvent<HTMLInputElement>,
  ) => {
    if (e.getModifierState) setCapsLock(e.getModifierState("CapsLock"));
  };

  return (
    <div className="group relative">
      <div className={iconWrap}>
        <Lock className="h-4 w-4" aria-hidden="true" />
      </div>
      <input
        ref={ref}
        type={show ? "text" : "password"}
        onKeyDown={trackCaps}
        onKeyUp={trackCaps}
        onClick={trackCaps}
        className={cx(
          inputBase,
          "pl-10",
          capsLock ? "pr-32" : "pr-11",
          error && inputError,
          className,
        )}
        {...props}
      />
      <div className="absolute inset-y-0 right-0 flex items-center pr-3">
        {capsLock && (
          <span className="mr-2 rounded-sm bg-amber-100 px-1.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider text-amber-700">
            Caps Lock
          </span>
        )}
        <button
          type="button"
          onClick={() => setShow((prev) => !prev)}
          className="flex items-center text-slate-400 transition-colors hover:text-slate-600 focus:outline-hidden focus-visible:text-violet-600"
          aria-label={show ? t("Ocultar senha") : t("Mostrar senha")}
        >
          {show ? (
            <EyeOff className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Eye className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </div>
    </div>
  );
});
PasswordInput.displayName = "PasswordInput";

/* ───────────────────────────────  Alertas  ───────────────────────────── */

export type AuthAlertVariant = "error" | "warning" | "info" | "success";

const alertConfig: Record<
  AuthAlertVariant,
  { box: string; icon: React.ReactNode }
> = {
  error: {
    box: "bg-rose-50 border-rose-200 text-rose-900",
    icon: <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />,
  },
  warning: {
    box: "bg-amber-50 border-amber-200 text-amber-900",
    icon: <Clock className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />,
  },
  info: {
    box: "bg-violet-50 border-violet-200 text-violet-900",
    icon: <Info className="mt-0.5 h-5 w-5 shrink-0 text-violet-600" />,
  },
  success: {
    box: "bg-emerald-50 border-emerald-200 text-emerald-900",
    icon: <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />,
  },
};

interface AuthAlertProps {
  variant?: AuthAlertVariant;
  title?: string;
  message: string;
  /** Ações/links exibidos abaixo da mensagem. */
  children?: React.ReactNode;
}

export const AuthAlert: React.FC<AuthAlertProps> = ({
  variant = "error",
  title,
  message,
  children,
}) => {
  const config = alertConfig[variant];
  return (
    <div
      role="alert"
      aria-live="polite"
      className={cx(
        "flex animate-fade-in items-start gap-3 rounded-xl border p-4 text-sm",
        config.box,
      )}
    >
      {config.icon}
      <div className="flex-1 space-y-0.5">
        {title && <p className="font-semibold leading-snug">{title}</p>}
        <p className="leading-relaxed opacity-90">{message}</p>
        {children && <div className="pt-1.5">{children}</div>}
      </div>
    </div>
  );
};

/* ───────────────────────  Botões, links e status  ────────────────────── */

interface AuthButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  loadingText?: string;
  /** Mostra uma seta ao final do texto (desliza no hover). */
  arrow?: boolean;
}

export const AuthButton: React.FC<AuthButtonProps> = ({
  loading,
  loadingText,
  arrow,
  disabled,
  className,
  children,
  ...props
}) => (
  <button
    disabled={disabled || loading}
    className={cx(
      "virtus-auth-btn group flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-purple-700 text-sm font-medium text-white shadow-xs transition-all",
      "hover:bg-[#6d28d9] active:scale-[0.98] focus:outline-hidden focus-visible:ring-[3px] focus-visible:ring-violet-500/30",
      "disabled:cursor-not-allowed disabled:opacity-60 disabled:active:scale-100",
      className,
    )}
    {...props}
  >
    {loading ? (
      <>
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>{loadingText}</span>
      </>
    ) : (
      <>
        {children}
        {arrow && (
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        )}
      </>
    )}
  </button>
);

export const AuthSecondaryButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement>
> = ({ className, ...props }) => (
  <button
    className={cx(
      "flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 shadow-xs transition-all",
      "hover:border-slate-400 hover:bg-slate-50 active:scale-[0.98] focus:outline-hidden focus-visible:ring-[3px] focus-visible:ring-violet-500/20",
      className,
    )}
    {...props}
  />
);

export const AuthLink: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement>
> = ({ className, ...props }) => (
  <button
    type="button"
    className={cx(
      "font-semibold text-purple-700 transition-colors hover:text-[#6d28d9] hover:underline focus:outline-hidden focus-visible:underline",
      className,
    )}
    {...props}
  />
);

interface AuthStatusProps {
  variant?: "success" | "info";
  title: string;
  children?: React.ReactNode;
}

/** Cabeçalho das telas de resultado (sucesso/instruções). */
export const AuthStatus: React.FC<AuthStatusProps> = ({
  variant = "success",
  title,
  children,
}) => (
  <div className="space-y-3 text-center sm:text-left">
    <div
      className={cx(
        "inline-flex h-12 w-12 items-center justify-center rounded-full",
        variant === "success"
          ? "bg-emerald-100 text-emerald-600"
          : "bg-violet-100 text-violet-600",
      )}
    >
      {variant === "success" ? (
        <CheckCircle2 className="h-6 w-6" />
      ) : (
        <Info className="h-6 w-6" />
      )}
    </div>
    <h1 className="text-2xl font-bold leading-tight tracking-tight text-slate-900">
      {title}
    </h1>
    {children && (
      <div className="space-y-3 text-sm leading-relaxed text-slate-500">
        {children}
      </div>
    )}
  </div>
);
